<template>
    <div
        class="dk-search-vehicle-card tw-relative tw-flex max-lg:tw-flex-col tw-w-full tw-min-h-[286px] tw-bg-white tw-rounded tw-border tw-border-solid tw-border-slate-100"
    >
        <div
            class="dk-search-vehicle-card__media-container tw-group tw-relative lg:tw-absolute tw-top-0 tw-left-0 tw-h-full tw-w-full lg:tw-max-w-[332px]"
        >
            <dk-slider
                :swiper-opts="swiperOptions"
                class="dk-search-vehicle-card__slider tw-relative tw-h-full"
                slider-controls-classes="tw-mt-3"
                @slide-change="trackEventSwitchSlide"
            >
                <dk-slide v-for="(image, index) in galleryImagesList" :key="index">
                    <figure
                        ref="slideImageFigure"
                        class="dk-search-vehicle-card__image-figure tw-h-[176px] lg:tw-h-full"
                        :class="{ 'tw-relative': image.uspData }"
                    >
                        <img
                            class="tw-w-full tw-h-full tw-object-cover"
                            :class="{
                                'swiper-lazy': index !== 0,
                                lazyload: index === 0,
                            }"
                            :src="fakeImg"
                            :data-src="getSrc(image.src)"
                            :data-srcset="getSrcset(image.src)"
                            :data-sizes="sizes"
                            :alt="item.title"
                            @load="onImageSlideLoad(index, $event)"
                        >
                        <DkUspOverlay
                            v-if="image.uspData"
                            class="dk-search-vehicle-card__usp"
                            :title="image.uspData.title"
                            :is-in-search-card="true"
                        />
                    </figure>
                </dk-slide>
                <template slot="button-prev" slot-scope="{ _uid }">
                    <div
                        :class="
                            `swiper-custom-button-prev prev-${_uid} tw-absolute tw-z-10 tw-left-1 tw-top-1/2 -tw-translate-y-1/2 tw-hidden group-hover:lg:tw-block`
                        "
                    >
                        <dk-button
                            class="tw-cursor-pointer tw-text-negative hover:tw-text-negative-subtle tw-duration-300 focus:!tw-ring-offset-0 focus:!tw-ring-transparent"
                            type="button"
                            is-icon-only
                            variant="ghost"
                        >
                            <dk-icon name="ds-chevron-left" size="s" />
                        </dk-button>
                    </div>
                </template>
                <template slot="button-next" slot-scope="{ _uid }">
                    <div
                        :class="
                            `swiper-custom-button-next next-${_uid} tw-absolute tw-z-10 tw-right-1 tw-top-1/2 -tw-translate-y-1/2 tw-hidden group-hover:lg:tw-block`
                        "
                    >
                        <dk-button
                            class="tw-cursor-pointer tw-text-negative hover:tw-text-negative-subtle tw-duration-300 focus:!tw-ring-offset-0 focus:!tw-ring-transparent"
                            type="button"
                            is-icon-only
                            variant="ghost"
                        >
                            <dk-icon name="ds-chevron-right" size="s" />
                        </dk-button>
                    </div>
                </template>
            </dk-slider>
            <dk-button
                class="dk-search-vehicle-card__button-compare tw-absolute tw-left-4 tw-bottom-4 tw-z-10 tw-flex tw-gap-2 tw-bg-white tw-border-black !tw-px-1"
                :variant="'tertiary'"
                :is-small="true"
                @click="openCompareModal"
            >
                <dk-icon name="ds-compare" size="xs" color="tertiary" />
                <span>{{ $t('compare.compare') }}</span>
            </dk-button>
            <div
                v-if="galleryImagesList.length > 0"
                class="dk-search-vehicle-card__images-count tw-absolute tw-right-4 tw-bottom-4 tw-z-10 !tw-h-8"
            >
                <media-counter>
                    <dk-media-swipe-viewer
                        class="dk-search-vehicle-card__button-gallery tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-py-[2px] tw-px-2 tw-text-sm tw-leading-sm lg:tw-leading-base lg:tw-text-base tw-bg-slate-800 tw-text-white tw-opacity-[.72] lg:hover:tw-opacity-[.88] tw-h-full tw-cursor-pointer"
                        :swipe-options="{ history: false }"
                        :items="mediaSwiperImagesList"
                        :usp-data="uspData"
                        @show="openMediaGallery('src', $event)"
                    >
                        <dk-icon class="tw-mt-px" name="ds-camera-filled" size="xs" color="tertiary" />
                        <span>{{ galleryImagesList.length }}</span>
                    </dk-media-swipe-viewer>
                </media-counter>
            </div>
        </div>
        <div
            class="dk-search-vehicle-card__info-container tw-w-full tw-p-4 lg:tw-pl-[348px] tw-gap-3"
            :class="{ 'lg:tw-flex lg:tw-flex-col lg:tw-justify-between': !item.pricing.length && showClickoutBox }"
        >
            <div
                class="dk-search-vehicle-card__info-headline tw-flex max-lg:tw-flex-col tw-w-full tw-justify-between tw-items-start"
            >
                <div class="dk-search-vehicle-card__info-title lg:tw-mr-4">
                    <h2 class="tw-text-xl lg:tw-text-3xl tw-leading-lg lg:tw-leading-xl tw-font-semibold tw-text-black">
                        {{ item.title }}
                    </h2>
                    <div
                        v-if="hasLegalCompliances"
                        class="dk-search-vehicle-card__legal-compliances tw-mt-2 tw-text-2xs tw-leading-2xs tw-text-placeholder"
                    >
                        <template v-for="(compliance, i) in legalCompliances">
                            <span
                                v-if="compliance.value"
                                :key="`compliance-${i}`"
                                class="dk-search-vehicle-card__legal-compliances-item"
                            >
                                <span class="tw-mr-1">{{ compliance.label }}</span>
                                <span>{{ compliance.value }}</span>
                            </span>
                            <span v-if="i !== legalCompliances.length - 1" :key="i">&nbsp;·&nbsp;</span>
                        </template>
                    </div>
                    <div
                        class="dk-search-vehicle-card__pricing tw-flex tw-items-center max-lg:tw-justify-between tw-mt-2"
                    >
                        <span
                            class="dk-search-vehicle-card__pricing-label tw-mr-2 tw-text-xs tw-leading-xs tw-text-base-subtle"
                        >
                            {{ item.priceFrom.label }}
                        </span>
                        <span
                            class="dk-search-vehicle-card__pricing-price tw-text-lg lg:tw-text-xl tw-leading-base lg:tw-leading-lg !tw-font-semibold tw-text-black"
                        >
                            {{ item.priceFrom.finalPrice || item.priceFrom.originalPrice }}
                            <small
                                v-if="item.priceFrom.priceSuffix"
                                class="tw-text-xs tw-leading-xs tw-font-normal tw-text-base-subtle"
                            >
                                {{ item.priceFrom.priceSuffix }}
                            </small>
                        </span>
                    </div>
                </div>
                <dk-button
                    class="dk-search-vehicle-card__cta-new tw-block tw-whitespace-nowrap tw-mt-2 lg:tw-mt-0 tw-w-full lg:tw-max-w-[200px] max-lg:!tw-text-sm"
                    :href="item.offerPageHref"
                    target="_blank"
                    @click="handleCTAClick()"
                >
                    {{ $t('ds-vehicle-card.discover-more') }}
                </dk-button>
            </div>
            <div v-if="item.pricing.length" class="dk-search-vehicle-card__offers tw-mt-4 lg:tw-mt-3">
                <div class="dk-search-vehicle-card__offers-item tw-flex tw-flex-col tw-gap-4 lg:tw-gap-3">
                    <dk-divider class="tw-w-full group-hover:tw-border-slate-500 tw-duration-100" />
                    <dk-offer-item
                        :item="item.pricing[0]"
                        :oem-redirect-url="oemRedirectURL"
                        @click-select-promotion="onSelectPromotion(item.submodelId, item.pricing[0].offerId)"
                    />
                </div>
                <dk-accordion
                    v-if="restOffers && restOffers.length > 0"
                    class="dk-search-vehicle-card__other-offers tw-mt-4"
                    :title="$tc('ds-vehicle-card.other-offers-available', restOffers.length, { n: restOffers.length })"
                    is-vehicle-card
                >
                    <div v-for="(offer, i) in restOffers" :key="i" class="vehicle-card__offers-item">
                        <dk-divider
                            v-if="i !== 0"
                            class="tw-w-full group-hover:tw-border-slate-500 tw-duration-100 tw-my-3"
                        />
                        <dk-offer-item
                            :item="offer"
                            :oem-redirect-url="oemRedirectURL"
                            @click-select-promotion="onSelectPromotion(item.submodelId, offer.offerId)"
                        />
                    </div>
                </dk-accordion>
            </div>
            <clickout-box
                v-if="showClickoutBox"
                class="dk-search-vehicle-card__clickout !tw-mt-4 lg:!tw-mt-3"
                :is-in-card="true"
                :description="item.clickout_section.description"
                description-font-size="s"
                :cta-text="item.clickout_section.cta"
                :url="item.clickout_section.oem_url"
                origin-page-name="Search page"
                cta-classes="lg:!tw-w-[200px] lg:!tw-px-6"
                :show-icon="false"
            />
        </div>
    </div>
</template>

<script>
import { fakeImg } from '@v4/common/utils';
import { getPrismImageUrl } from '@v4/common/utils/image';
import gaEventHandler from '@v4/common/components/mixins/google-analytics';
import { modalServiceCompareVehicle, modalEmissionConsumption } from '@v4/common/components/mixins/modal-service';
import { dkModalAware } from '@v4/common/components/mixins/modal';
import { isEmpty } from 'lodash';
import { redirect } from '@v4/http/request';

export default {
    name: 'DkSearchVehicleCard',
    components: {
        DkButton: () => ({
            component: import(/* webpackChunkName: "DkButton-vue" */ '@v4/common/components/atoms/DkButton.vue'),
        }),
        DkIcon: () => ({
            component: import(/* webpackChunkName: "DkIcon-vue" */ '@v4/common/components/atoms/DkIcon.vue'),
        }),
        DkDivider: () => ({
            component: import(/* webpackChunkName: "DkDivider-vue" */ '@v4/common/components/atoms/DkDivider.vue'),
        }),
        DkOfferItem: () => ({
            component: import(
                /* webpackChunkName: "DkOfferItem-vue", webpackPreload: true */ '@v4/common/components/molecules/VehicleCard/components/molecules/OfferItem/DkOfferItem.vue'
            ),
        }),
        DkAccordion: () => ({
            component: import(
                /* webpackChunkName: "DkAccordion-vue", webpackPreload: true */ '@v4/common/components/molecules/DkAccordionDST.vue'
            ),
        }),
        ClickoutBox: () => ({
            component: import(
                /* webpackChunkName: "ClickoutBox-vue" */ '@v4/common/components/molecules/ClickoutBox.vue'
            ),
        }),
        DkSlide: () => ({
            component: import(/* webpackChunkName: "DkSlide-vue" */ '@v4/common/components/atoms/DkSlide/DkSlide.vue'),
        }),
        DkSlider: () => ({
            component: import(/* webpackChunkName: "DkSlider-vue" */ '@v4/common/components/molecules/DkSliderDST.vue'),
        }),
        MediaCounter: () => ({
            component: import(
                /* webpackChunkName: "MediaCounter-vue" */ '@v4/common/components/molecules/MediaCounter'
            ),
        }),
        DkMediaSwipeViewer: () => ({
            component: import(
                /* webpackChunkName: "DkMediaSwipeViewer-vue", webpackPreload: true */ '@v4/common/components/organisms/DkMediaSwipeViewer/DkMediaSwipeViewer.vue'
            ),
        }),
        DkUspOverlay: () => ({
            component: import(
                /* webpackChunkName: "DkUspOverlay-vue", webpackPreload: true */ '@v4/common/components/atoms/DkUSPOverlay.vue'
            ),
        }),
    },
    mixins: [dkModalAware, gaEventHandler, modalServiceCompareVehicle, modalEmissionConsumption],
    props: {
        /**
         * Vehicle info object.
         */
        item: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Google Analytics events
         */
        gaEvents: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            fakeImg,
            galleryCurrentIndex: 1,
            swiperOptions: {
                slidesPerView: 1,
                pagination: false,
                loop: true,
                preloadImages: false,
                lazy: {
                    loadPrevNext: false,
                },
                on: {
                    slideChange: () => {
                        const el = this.$el.querySelector('.swiper-container');
                        this.galleryCurrentIndex = el && el.swiper ? el.swiper.realIndex + 1 : 1;

                        /**
                         * Swiper creates duplicated slide items when loop options is true
                         * and the duplicated slides are not updated in the loop mode
                         * @see https://github.com/kidjp85/react-id-swiper/issues/379
                         *
                         * Because of that, every time when the image of the original slide-item is lazyloaded
                         * and the .swiper-lazy-preloader is removed from the current original slide,
                         * the duplicated slide-item is not updated and still keeps the .swiper-lazy-preloader inside.
                         *
                         * The solution for the fix is checking if the original slide-item is lazyloaded
                         * and removing the .swiper-lazy-preloader from the duplicated one on each slide change.
                         */
                        if (!(el && el.swiper)) {
                            return;
                        }
                        const originalIsLasyloaded = el.querySelector(
                            `.swiper-slide[data-swiper-slide-index="${el.swiper.realIndex}"] img.lazyloaded`
                        );

                        if (!originalIsLasyloaded) {
                            return;
                        }
                        const duplicatedEl = el.querySelector(
                            `.swiper-slide-duplicate[data-swiper-slide-index="${el.swiper.realIndex}"]`
                        );
                        const loaderElm = duplicatedEl && duplicatedEl.querySelector('.swiper-lazy-preloader');
                        if (loaderElm) {
                            loaderElm.parentElement.removeChild(loaderElm);
                        }
                    },
                },
            },
        };
    },
    computed: {
        galleryImagesList() {
            if (this.item.images && this.item.images.length) {
                const images = this.item.images.map((item, index) => ({
                    src: item.src,
                    ...(index === 1 && this.item.uspData && { uspData: this.item.uspData }),
                }));

                return images;
            }
            return [this.item.imageSrc];
        },
        mediaSwiperImagesList() {
            const dimensions = { h: 600, w: 600 };
            if (this.item.images && this.item.images.length) {
                return this.item.images.map((item) => ({
                    ...dimensions,
                    src: item.src,
                    ...(item.isUSP && { isUSP: true }),
                }));
            }
            return [
                {
                    ...dimensions,
                    src: this.item.imageSrc,
                },
            ];
        },
        hasLegalCompliances() {
            return (
                this.item.detailsEmissionConsumption &&
                (this.item.detailsEmissionConsumption.co2 ||
                    this.item.detailsEmissionConsumption.combinedConsumption ||
                    this.item.detailsEmissionConsumption.combinedMethaneConsumption ||
                    this.item.detailsEmissionConsumption.efficiencyClass)
            );
        },
        legalCompliances() {
            const legalCompliances = { ...this.item.detailsEmissionConsumption };
            delete legalCompliances.disclaimerModal;
            return Object.entries(legalCompliances).map(([key, value]) => ({
                key,
                ...value,
            }));
        },
        co2() {
            return this.item.detailsEmissionConsumption && this.item.detailsEmissionConsumption.co2;
        },
        combinedConsumption() {
            return this.item.detailsEmissionConsumption && this.item.detailsEmissionConsumption.combinedConsumption;
        },
        combinedMethaneConsumption() {
            return (
                this.item.detailsEmissionConsumption && this.item.detailsEmissionConsumption.combinedMethaneConsumption
            );
        },
        combinedElectricConsumption() {
            return (
                this.item.detailsEmissionConsumption && this.item.detailsEmissionConsumption.combinedElectricConsumption
            );
        },
        showClickoutBox() {
            return this.item.clickout_section && !isEmpty(this.item.clickout_section);
        },
        oemRedirectURL() {
            return this.showClickoutBox && this.item.clickout_section.boost_clickout
                ? this.item.clickout_section.oem_url
                : null;
        },
        restOffers() {
            return this.item.pricing.slice(1);
        },
        sizes() {
            return `
                (max-width: 360px) 310px,
                (max-width: 400px) 400px,
                (max-width: 1024px) 800px,
                (min-width: 1024px) 400px,
            `;
        },
        uspData() {
            return this.item.uspData;
        },
    },
    methods: {
        onImageSlideLoad(index, { target }) {
            if (index !== 0 || target.classList.contains('lazyload')) {
                return;
            }

            const slideImageFigureElm = this.$refs.slideImageFigure ? this.$refs.slideImageFigure[index] : null;
            const loaderElm = slideImageFigureElm && slideImageFigureElm.nextElementSibling;

            if (loaderElm && loaderElm.classList.contains('swiper-lazy-preloader')) {
                loaderElm.parentElement.removeChild(loaderElm);
            }
        },
        getSrc(src) {
            return getPrismImageUrl(src, 290);
        },
        getSrcset(src) {
            return `${getPrismImageUrl(src, 290)} 290w,
                ${getPrismImageUrl(src, 400)} 400w,
                ${getPrismImageUrl(src, 800)} 800w
            `;
        },
        openCompareModal() {
            this.trackCompareButtonClick();

            this.openCompareVehicleModalAndHandleClick(
                null,
                this.item,
                [],
                `${this.$t('compare.compare')} ${this.item.title}`,
                this.$t('compare.compare')
            );
        },
        openMediaGallery(source, { photoSwipe }) {
            if (photoSwipe && typeof photoSwipe.goTo === 'function') {
                photoSwipe.goTo(this.galleryCurrentIndex - 1 || 0);
            }
        },
        handleCTAClick() {
            this.trackCtaButton();

            if (this.oemRedirectURL) {
                // Allow the track requests to finish
                setTimeout(() => {
                    redirect(this.oemRedirectURL);
                }, 500);
            }
        },
        onSelectPromotion(offerId, submodelId) {
            this.$emit('click-select-promotion', offerId, submodelId);
        },
        trackEventSwitchSlide() {
            this.pushGtagEvent('click_switch_slide', this.item.submodelId, this.gaEvents);
        },
        trackCompareButtonClick() {
            this.pushGaEvent('Search page', 'vehicle compare click', 'Search page card - Compare button click');
        },
        trackCtaButton() {
            this.pushGaEvent('Search page', 'vehicle cta click', 'Search page card - CTA button');
            this.pushGtagEvent('select_item', this.item.submodelId, this.gaEvents);
        },
    },
};
</script>
